import { AiPanelAffectedGene, AiPanelTrait, DualShape } from '../../../../../types/geneCodes/AiPanelReportTypes';

// ⚠️ THE "N/A CARD" FIX FOR ROWS THAT ARE ALREADY STORED.
//
// The Anthropic API does not validate tool input against `input_schema`, so the schema's
// key names were advisory. Measured on `panel_ai_data` 2026-08-24: 56 of 88 non-empty JSON
// rows (64%) carry drifted trait keys, across ~25 distinct invented shapes. `xoe-agent`'s
// prompt now names every key and its parser repairs what still slips through — but that
// only helps rows written from now on. These 56 already exist, and gene.codes never showed
// an N/A card precisely because it read both vocabularies on every trait
// (temp/gene.codes/js/pubscripts1.js:347-361). This is that, kept.
const ALIASES = {
	name: ['name', 'trait', 'trait_name'],
	severity: ['severity', 'genetic_risk_level', 'risk_level', 'intervention_priority'],
	analysis: [
		'analysis', 'physiological_implications', 'physiological_impact',
		'description', 'overall_assessment', 'risk_assessment', 'key_findings',
	],
	gene_function_impact: [
		'gene_function_impact', 'genetic_impact', 'functional_consequences',
		'overall_impact', 'genetic_burden',
	],
	affected_genes: [
		'affected_genes', 'genes_affected', 'genes_analyzed', 'genes_involved',
		'genes_with_risk_alleles', 'key_variants', 'high_risk_variants',
	],
	risk_allele_count: ['risk_allele_count'],
	genetic_risk_percentage: ['genetic_risk_percentage', 'genetic_risk'],
} as const;

const firstPresent = (source: Record<string, unknown>, names: readonly string[]): unknown => {
	for (const name of names) {
		const value = source[name];
		if (value !== undefined && value !== null && value !== '' && !(Array.isArray(value) && value.length === 0)) {
			return value;
		}
	}
	return undefined;
};

// gene.codes accepted three gene shapes; all three normalise to `{gene, snps[]}`.
const normalizeGene = (entry: unknown): AiPanelAffectedGene | null => {
	if (typeof entry === 'string') return entry ? { gene: entry, snps: [] } : null;
	if (!entry || typeof entry !== 'object') return null;

	const record = entry as Record<string, unknown>;
	const gene = record.gene ?? record.name;
	if (typeof gene !== 'string' || !gene) return null;

	if (Array.isArray(record.snps)) return { gene, snps: record.snps };

	const rsid = record.rsid ?? record.rs;
	return {
		gene,
		snps: typeof rsid === 'string' && rsid
			? [{ rsid, genotype: record.genotype as string, risk_alleles: record.risk_alleles as number }]
			: [],
	};
};

export const normalizeTrait = (trait: DualShape<AiPanelTrait>): DualShape<AiPanelTrait> => {
	if (typeof trait === 'string' || !trait || typeof trait !== 'object') return trait;

	const source = trait as Record<string, unknown>;
	const out: Record<string, unknown> = {};

	(Object.keys(ALIASES) as Array<keyof typeof ALIASES>).forEach((canonical) => {
		const value = firstPresent(source, ALIASES[canonical]);
		if (value !== undefined) out[canonical] = value;
	});

	if (Array.isArray(out.affected_genes)) {
		out.affected_genes = out.affected_genes.map(normalizeGene).filter(Boolean);
	} else if (out.affected_genes !== undefined) {
		out.affected_genes = [];
	}

	// The badge colour and the impact sort both key on a lowercase string.
	if (typeof out.severity === 'string') out.severity = out.severity.trim().toLowerCase();

	const claimed = new Set(Object.values(ALIASES).flat() as string[]);
	Object.keys(source).forEach((key) => {
		if (!(key in out) && !claimed.has(key)) out[key] = source[key];
	});

	return out as AiPanelTrait;
};
